import { useMemo } from "react";
import { Box, Portal, useMediaQuery, useTheme } from "@mui/material";
import { motion } from "motion/react";

const FALLBACK_COLORS = ["#3A7DC9", "#5BB8F5", "#1A3F7A", "#F4A11A", "#FFC14D", "#19A874", "#E14B5A"];

const Balloon = ({ color, size }) => (
	<svg width={size} height={size * 1.45} viewBox="0 0 60 87" fill="none" aria-hidden="true">
		<ellipse cx="30" cy="30" rx="24" ry="29" fill={color} />
		<ellipse cx="21" cy="19" rx="6" ry="9.5" fill="#FFFFFF" opacity="0.3" transform="rotate(-22 21 19)" />
		<path d="M30 58.5 L25 66 H35 Z" fill={color} />
		<path d="M30 66 C34 72 26 79 30 87" stroke={color} strokeWidth="1.4" strokeLinecap="round" fill="none" opacity="0.55" />
	</svg>
);

const Balloons = ({ duration = 8000, active = true, colors = FALLBACK_COLORS }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const count = isMobile ? 7 : 12;

	// Generated once per mount — regenerating on render would restart every
	// balloon's rise mid-flight.
	const balloons = useMemo(
		() =>
			Array.from({ length: count }, (_, i) => ({
				id: i,
				color: colors[i % colors.length],
				// Spread across the viewport with a little jitter so they don't
				// read as evenly spaced columns.
				left: `${(i / count) * 100 + Math.random() * (60 / count)}%`,
				size: isMobile ? 34 + Math.random() * 16 : 44 + Math.random() * 26,
				delay: Math.random() * (duration / 1000) * 0.45,
				rise: 7 + Math.random() * 4,
				repeatDelay: Math.random() * 2.5,
				drift: (Math.random() - 0.5) * 90,
				sway: 12 + Math.random() * 14,
			})),
		[count, isMobile, duration, colors]
	);

	if (!active) return null;

	return (
		<Portal>
			<Box
				aria-hidden="true"
				sx={{
					position: "fixed",
					inset: 0,
					overflow: "hidden",
					pointerEvents: "none",
					zIndex: (t) => t.zIndex.modal + 1,
				}}
			>
				{balloons.map((b) => (
					<motion.div
						key={b.id}
						initial={{ y: "10vh", x: 0, opacity: 0 }}
						animate={{ y: "-125vh", x: b.drift, opacity: [0, 1, 1, 0.9] }}
						transition={{
							duration: b.rise,
							delay: b.delay,
							ease: "easeOut",
							// The modal now waits on the agent, so the balloons loop
							// rather than leaving a static card once they clear frame.
							repeat: Infinity,
							repeatDelay: b.repeatDelay,
							opacity: {
								duration: b.rise,
								times: [0, 0.08, 0.85, 1],
								repeat: Infinity,
								repeatDelay: b.repeatDelay,
							},
						}}
						style={{ position: "absolute", bottom: 0, left: b.left, willChange: "transform" }}
					>
						{/* Sway is its own nested motion so it loops independently of the rise. */}
						<motion.div
							animate={{ x: [-b.sway, b.sway, -b.sway] }}
							transition={{ duration: 2.6 + Math.random(), repeat: Infinity, ease: "easeInOut" }}
						>
							<Balloon color={b.color} size={b.size} />
						</motion.div>
					</motion.div>
				))}
			</Box>
		</Portal>
	);
};

export default Balloons;
